import { lazy, Suspense, useCallback, useMemo, useState, type ChangeEvent } from 'react'
import { Alert, Button, Card, Empty, Input, Popconfirm, Segmented, Space, Spin, Tag, Tooltip, Typography } from 'antd'
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons'
import type { useConfiguration } from '../hooks/useConfiguration'
import { filePresentation, type EditorView } from '../config/presentation'

const ISSUE_TITLES = { unknown: '应用结果待确认，请重试确认', conflict: '配置已变化', failure: '配置应用失败' }
const OUTCOME_TITLES = { active: '已应用', superseded: '已有后续配置，本次结果不再是当前配置' }
const STATUS_TITLES = { active: '已应用', initial: '未应用' }
const SAVE_ICON = <SaveOutlined aria-hidden />
const RELOAD_ICON = <ReloadOutlined aria-hidden />
const MarkdownEditor = lazy(() => import('./MarkdownEditor').then((module) => ({ default: module.MarkdownEditor })))
const StructuredEditor = lazy(() => import('./StructuredEditor').then((module) => ({ default: module.StructuredEditor })))

export const FileEditor = ({ model }: { model: ReturnType<typeof useConfiguration> }) => {
  const presentation = useMemo(() => filePresentation(model.file?.path ?? '', model.content), [model.content, model.file?.path])
  const [requestedView, setView] = useState<EditorView>(() => presentation.defaultView)
  const [sourceOpen, setSourceOpen] = useState(false)
  const [toolbarContainer, setToolbarContainer] = useState<HTMLDivElement | null>(null)
  const view = requestedView
  const historyContainer = sourceOpen ? null : toolbarContainer
  const advanced = useCallback(() => setSourceOpen(true), [])
  const selectView = useCallback((value: string | number) => {
    setSourceOpen(value === 'source')
    if (value !== 'source') setView(value as EditorView)
  }, [])
  const { change, save, reload } = model
  const contentChanged = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => change(event.target.value), [change])
  const apply = useCallback(() => { void save().catch(() => undefined) }, [save])
  const refresh = useCallback(() => { void reload(true).catch(() => undefined) }, [reload])
  const reloadButton = <Button onClick={model.changed ? undefined : refresh} icon={RELOAD_ICON}
    aria-label="重新读取" disabled={model.saving} />
  const reloadAction = model.changed ? <Popconfirm title="放弃当前文件的未保存修改？" okText="放弃并读取"
    cancelText="保留修改" onConfirm={refresh}>{reloadButton}</Popconfirm> : reloadButton
  const saveLabel = model.issue?.kind === 'unknown' ? '重试确认' : '保存并应用'
  const actions = <Space className="instance-administration__editor-actions">
    <Tooltip title="重新读取"><span>{reloadAction}</span></Tooltip>
    <Tooltip title={saveLabel}><span><Button type="primary" icon={SAVE_ICON} onClick={apply} loading={model.saving}
      aria-label={saveLabel}
      disabled={!model.snapshot || !!model.readError || (!model.changed && model.snapshot.status !== 'initial') || model.issue?.kind === 'conflict'} /></span></Tooltip>
  </Space>
  const heading = <div className="instance-administration__file-heading">
    <Tooltip title={model.file?.path}><Typography.Text strong>{model.catalog?.entries.find((entry) => entry.path === model.file?.path)?.label ?? model.file?.path ?? '配置文件'}</Typography.Text></Tooltip>
    {model.snapshot ? <Tag>{STATUS_TITLES[model.snapshot.status]}</Tag> : null}
    {model.changed ? <Tag color="processing">有未保存修改</Tag> : null}
  </div>
  return <Card title={heading} className="instance-administration__file-editor" loading={model.loading && !model.file}>
    {model.issue ? <Alert type={model.issue.kind === 'unknown' ? 'warning' : 'error'} showIcon
      title={ISSUE_TITLES[model.issue.kind]} description={model.issue.message} role="alert" /> : null}
    {model.outcome ? <Alert type={model.outcome.status === 'active' ? 'success' : 'info'} showIcon
      title={OUTCOME_TITLES[model.outcome.status]} role="status" aria-label="配置应用结果" /> : null}
    <div className="instance-administration__editor-navigation" role="toolbar" aria-label="配置编辑工具栏">
      {model.file ? <Segmented options={presentation.views} value={sourceOpen ? 'source' : view} onChange={selectView} aria-label="编辑方式" /> : null}
      <div ref={setToolbarContainer} className="instance-administration__editor-history" />
      {actions}
    </div>
    {model.file && view === 'table' ? <div hidden={sourceOpen}><Suspense fallback={<Spin />}>
      <StructuredEditor path={model.file.path} content={model.content} onChange={change}
        disabled={model.saving || sourceOpen} onAdvanced={advanced} toolbarContainer={historyContainer} />
    </Suspense></div> : null}
    {model.file && !sourceOpen && (view === 'preview' || view === 'rich_text') ? <Suspense fallback={<Spin />}>
        <MarkdownEditor content={model.content} onChange={change} disabled={model.saving} preview={view === 'preview'} />
      </Suspense> : model.file && sourceOpen ? <Input.TextArea aria-label="配置内容" value={model.content} onChange={contentChanged}
      disabled={model.saving} spellCheck={false} className="instance-administration__source-input" />
      : !model.file ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="请选择配置文件" /> : null}
  </Card>
}
